<template>
  <section id="contact" class="position-relative py-6 py-md-8 contact-section">
    <!-- Background (soft gradient) -->
    <div class="bg-gradient"></div>

    <!-- Glass Card -->
    <div class="container position-relative">
      <div class="row justify-content-center">
        <div class="col-12 col-lg-10" style="padding-left: 12px;padding-right: 12px;">
          <div class="glass-card p-4 p-md-5 shadow-lg rounded-4">
            <div class="d-flex align-items-center gap-3 mb-4">
              <div class="icon-circle d-flex align-items-center justify-content-center wyget-icon gradient-emerald-glassy elevation">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Envelope">
                  <rect x="4" y="7" width="24" height="18" rx="3" stroke="#10b981" stroke-width="2"/>
                  <path d="M6 10L16 18L26 10" stroke="#10b981" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>

              </div>
              <div>
                <h2 class="h3 mb-1 fw-bold">{{ $t('contact.heading') }}</h2>
                <p class="mb-0 text-muted">{{ $t('contact.subheading') }}</p>
              </div>
            </div>

            <form @submit.prevent="onSubmit" novalidate>
              <div class="row g-3 g-md-4">
                <div class="col-md-6">
                  <label class="form-label">{{ $t('contact.labels.name') }}</label>
                  <input
                      type="text"
                      class="form-control "
                      :class="{ 'is-invalid': tried && !rules.required(model.name) }"
                      v-model.trim="model.name"
                      :placeholder="$t('contact.placeholders.name')"
                      autocomplete="name"
                      required
                  >
                  <div class="invalid-feedback">{{ $t('contact.validation.name_required') }}</div>
                </div>
                <div class="col-md-6">
                  <label class="form-label">{{ $t('contact.labels.company') }}</label>
                  <input
                      type="text"
                      class="form-control"
                      v-model.trim="model.company"
                      :placeholder="$t('contact.placeholders.company')"
                      autocomplete="organization"
                  >
                </div>
                <div class="col-md-6">
                  <label class="form-label">{{ $t('contact.labels.email') }}</label>
                  <input
                      type="email"
                      class="form-control "
                      :class="{ 'is-invalid': tried && !rules.email(model.email) }"
                      v-model.trim="model.email"
                      placeholder="you@company.com"
                      autocomplete="email"
                      required
                  >
                  <div class="invalid-feedback">{{ $t('contact.validation.email_invalid') }}</div>
                </div>

                <div class="col-md-6">
                  <label class="form-label">{{ $t('contact.labels.phone') }}</label>
                  <div class="input-group">
                    <select class="form-select flex-shrink-0 code-select" v-model="model.phoneNumberCode" :aria-label="$t('contact.labels.country_code')">
                      <!-- Mocked country codes; replace with your list -->
<!--                      <option value="+357">CY +357</option>-->
<!--                      <option value="+30">GR +30</option>-->
<!--                      <option value="+44">UK +44</option>-->
<!--                      <option value="+49">DE +49</option>-->
                      <option v-for="country in countryCodes" :key="country.code" :value="country">
                        {{ isWindows ? '' : isoCountryCodeToFlagEmoji(country.iso) }} {{ country.iso }} {{
                          country.code
                        }}
                      </option>
                    </select>
                    <input
                        type="tel"
                        class="form-control"
                        :class="{ 'is-invalid': tried && model.phoneNumber && (!rules.phone(model.phoneNumber) || !isPhoneValid) }"
                        v-model.trim="model.phoneNumber"
                        :placeholder="$t('contact.placeholders.phone')"
                        autocomplete="tel"
                        @change="normalizePhone"
                        @blur="normalizePhone"
                    >
                    <div class="invalid-feedback">{{ $t('contact.validation.phone_invalid') }}</div>
                  </div>
                </div>
                <div class="col-md-6">
                  <label class="form-label">{{ $t('contact.labels.subject') }}</label>
                  <input
                      type="text"
                      class="form-control "
                      :class="{ 'is-invalid': tried && !rules.required(model.subject) }"
                      v-model.trim="model.subject"
                      :placeholder="$t('contact.placeholders.subject')"
                      required
                  >
                  <div class="invalid-feedback">{{ $t('contact.validation.subject_required') }}</div>
                </div>
                <div class="col-12">
                  <label class="form-label">{{ $t('contact.labels.message') }}</label>
                  <textarea
                      class="form-control"
                      v-model.trim="model.description"
                      :placeholder="$t('contact.placeholders.message')"
                      rows="5"
                  />
                </div>

                <!-- Optional privacy note -->
                <div class="col-12 text-end">
                  <div class="form-text">
                    {{ $t('contact.privacy.prefix') }}
                    <a href="/terms.html" class="text-primary" target="_blank" rel="noopener">{{ $t('footer.terms') }}</a>
                    {{ $t('and') || 'and' }}
                    <a href="/privacy.html" class="text-primary" target="_blank" rel="noopener">{{ $t('footer.privacy') }}</a>.
                  </div>
                </div>

                <div class="col-12 text-end  gap-3">
                  <button v-if="!sent" class="btn btn-outline-glass btn-lg btn-glow px-4" type="submit" :disabled="submitting">
                    <span v-if="!submitting">{{ $t('contact.actions.send') }}</span>
                    <span v-else class="d-inline-flex align-items-center gap-2">
                      <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      {{ $t('contact.actions.sending') }}
                    </span>
                  </button>

                  <span v-if="sent" class="badge bg-success-subtle text-success-emphasis rounded-pill px-3 py-2">
                    <i class="bi bi-check2-circle me-1"></i> {{ $t('contact.success_badge') }}
                  </span>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import {getCountries, getCountryCallingCode, isValidNumber, parsePhoneNumberFromString} from "libphonenumber-js";
import FirebaseFunctionsHelper from "@/utils/firebaseFunctionsHelper";
import { trackGoogleAdsConversion } from "@/utils/googleAds";

const DEFAULT_PHONE_CODE = {code: "+357", text: "CY +357", iso: "CY"}

export default {
  name: 'ContactSection',
  data() {
    return {
      isWindows: navigator.platform.indexOf("Win") > -1,
      submitting: false,
      sent: false,
      tried: false,
      errorMessage: null,
      defaultPhoneCode: DEFAULT_PHONE_CODE,
      countryCodes: [],
      rules: {
        required: v => !!(v && String(v).trim().length > 0),
        email: v => /.+@.+\..+/.test(String(v || '')),
        phone: v => !v || /[0-9()\-\s]{5,}/.test(String(v || ''))
      },
      model:{
        name: null,
        company: "",
        email: null,
        subject: null,
        description: "",
        phoneNumber: "",
        phoneNumberCode: {...DEFAULT_PHONE_CODE}
      }
    }
  },
  computed:{
    isPhoneValid(){
      const number = String(this.model.phoneNumber || '').trim()
      if (!number) return true
      return isValidNumber(this.model.phoneNumberCode.code + number, this.model.phoneNumberCode.iso)
    },
  },
  mounted() {
    let countries = getCountries()

    countries.forEach(country => {
      let code = getCountryCallingCode(country)
      this.countryCodes.push({code: "+" + code, text: country + " +" + code, iso: country})
    })

    this.model.phoneNumberCode = this.getDefaultPhoneCode()
  },
  methods: {
    isoCountryCodeToFlagEmoji(country) {
      return String.fromCodePoint(...[...country.toUpperCase()].map(c => c.charCodeAt() + 0x1F1A5))
    },
    toast(icon = 'success', title = ''){
      const swal = this.$swal || (typeof window !== 'undefined' ? window.Swal : null)
      if (!swal) return console.log('[toast]', icon, title)
      const Toast = swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        didOpen: (t) => {
          t.addEventListener('mouseenter', swal.stopTimer)
          t.addEventListener('mouseleave', swal.resumeTimer)
        }
      })
      Toast.fire({ icon, title })
    },
    normalizePhone(){
      const raw = String(this.model.phoneNumber || '').trim()
      if (!raw) return
      try {
        // Try parse assuming it may be a full international number from autofill
        let p = parsePhoneNumberFromString(raw)
        if (!p || !p.isValid()) {
          // Fallback: try with currently selected country
          const iso = this.model.phoneNumberCode?.iso
          p = parsePhoneNumberFromString(raw, iso)
        }
        if (p && p.isValid()) {
          // Update country selector if country detected
          if (p.country) {
            const found = this.countryCodes.find(c => c.iso === p.country)
            if (found) this.model.phoneNumberCode = found
          }
          // Set national part in the input; keep only digits (lib already provides nationalNumber)
          if (p.nationalNumber) this.model.phoneNumber = String(p.nationalNumber)
        }
      } catch (e) {
        // no-op on parse failure
      }
    },
    getDefaultPhoneCode() {
      const found = this.countryCodes.find(c => c.iso === this.defaultPhoneCode.iso)
      return found || { ...this.defaultPhoneCode }
    },
    composeMessage() {
      const description = (this.model.description || '').trim()
      const company = (this.model.company || '').trim()
      const segments = []
      if (description) segments.push(description)
      if (company) segments.push(`Company: ${company}`)
      return segments.join('\n\n')
    },
    resetFormState() {
      this.model = {
        name: null,
        company: "",
        email: null,
        subject: null,
        description: "",
        phoneNumber: "",
        phoneNumberCode: this.getDefaultPhoneCode()
      }
      this.tried = false
      this.errorMessage = null
    },
    async onSubmit() {
      this.tried = true
      if (!this.rules.required(this.model.name)) return
      if (!this.rules.email(this.model.email)) return
      if (!this.rules.required(this.model.subject)) return
      if (this.model.phoneNumber && !this.rules.phone(this.model.phoneNumber)) return
      if (this.model.phoneNumber && !this.isPhoneValid) return

      this.submitting = true
      this.sent = false
      this.errorMessage = null

      try {
        // Mock API latency – real call follows
        const phone = String(this.model.phoneNumber || '').trim()
        const payload = {
          name: this.model.name,
          subject: this.model.subject,
          message: this.composeMessage(),
          email: this.model.email,
          phoneNumber: phone ? (this.model.phoneNumberCode.code + phone) : null
        }

        const data = await FirebaseFunctionsHelper.callFunction("company-contactUs", payload)

        if (data === null) {
          this.resetFormState()
          if (this.v$ && this.v$.$reset) this.v$.$reset()
          trackGoogleAdsConversion()

          this.toast('success', this.$t('contact.toast.success'))
          this.sent = true
        } else {
          this.errorMessage = this.$t(data.message)
          this.toast('error', this.errorMessage || this.$t('contact.toast.error'))
        }

      } catch (e) {
        console.error('Contact form error', e)
        this.toast('error', this.$t('contact.toast.error'))
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
/* Section spacing */
.py-6 { padding-top: 4rem; padding-bottom: 4rem }
.py-8 { padding-top: 6rem; padding-bottom: 6rem }

.contact-section { overflow: clip }

/* Soft gradient background */
.bg-gradient {
  position: absolute;
  inset: 0;
  background: radial-gradient(1200px 600px at 10% 10%, rgba(111, 66, 193, 0.25), rgba(111, 66, 193, 0) 60%),
  radial-gradient(1000px 500px at 90% 20%, rgba(13, 110, 253, 0.25), rgba(13, 110, 253, 0) 60%),
  linear-gradient(180deg, rgba(255,255,255,0.4), rgba(255,255,255,0.1))
;
  z-index: 0;
}

/* Glass card */
.glass-card {
  position: relative;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(14px) saturate(120%);
  -webkit-backdrop-filter: blur(14px) saturate(120%);
  border: 1px solid rgba(255, 255, 255, 0.35);
}

/* Shiny diagonal highlight */
.glass-card::after {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(135deg, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0.12) 28%, rgba(255,255,255,0) 55%);
}

.icon-circle {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  font-size: 1.25rem;
}

/* Make selects and inputs consistent height */
.code-select { max-width: 140px }

/* Utility */
.rounded-4 { border-radius: 1rem }

/* Bootstrap 5.3 utilities fallback if not present */
.bg-success-subtle { background-color: rgba(25, 135, 84, 0.15) !important }
.text-success-emphasis { color: #146c43 !important }

.text-primary {
  color: rgb(58 204 244) !important;
  text-decoration-color: rgb(58 204 244) !important;
}
</style>
